// Single source of truth for how every measurement is named, coloured and explained.
export const METRICS = {
  temperature: {
    key: 'temperature', label: 'Temperature', short: 'Temp', unit: '°C', color: '#f97316', decimals: 1, icon: 'Thermometer',
    help: 'Air temperature at canopy height, averaged over the sensor nodes of a station. Crops stop growing when it is too cold and suffer heat stress when it is too hot.',
  },
  humidity: {
    key: 'humidity', label: 'Relative humidity', short: 'Humidity', unit: '%', color: '#3b82f6', decimals: 0, icon: 'Droplets',
    help: 'How saturated the air is with water vapour. High humidity favours fungal diseases; very low humidity makes plants lose water faster.',
  },
  pressure: {
    key: 'pressure', label: 'Air pressure', short: 'Pressure', unit: 'hPa', color: '#8b5cf6', decimals: 0, icon: 'Gauge',
    help: 'Atmospheric pressure at the station. A fast drop usually announces rain or a storm front; a steady rise means settled weather.',
  },
  o2: {
    key: 'o2', label: 'Oxygen', short: 'O₂', unit: '%', color: '#14b8a6', decimals: 2, icon: 'Wind',
    help: 'Oxygen concentration of the air. It is almost constant outdoors (about 20.9 %). A drop is a sign of a sensor fault or a confined space.',
  },
  co2: {
    key: 'co2', label: 'Carbon dioxide', short: 'CO₂', unit: 'ppm', color: '#ca8a04', decimals: 0, icon: 'Cloud',
    help: 'CO₂ in parts per million. Normal outdoor air is about 400-450 ppm, higher at night when plants respire. Above 1 000 ppm points to a leak, fermentation or a greenhouse that needs ventilation.',
  },
  battery: {
    key: 'battery', label: 'Battery', short: 'Battery', unit: 'V', color: '#22c55e', decimals: 2, icon: 'BatteryCharging',
    help: 'Voltage of the station battery, charged by a solar panel. Below about 11 V the station may stop transmitting.',
  },
  vpd: {
    key: 'vpd', label: 'Vapour pressure deficit', short: 'VPD', unit: 'kPa', color: '#06b6d4', decimals: 2, icon: 'Sprout',
    help: 'VPD combines temperature and humidity into the "drying power" of the air. 0.4 to 1.6 kPa is the comfort zone for most crops; above 1.6 plants close their stomata and growth slows.',
  },
  dewPoint: {
    key: 'dewPoint', label: 'Dew point', short: 'Dew point', unit: '°C', color: '#0ea5e9', decimals: 1, icon: 'CloudFog',
    help: 'The temperature at which dew forms. When air temperature is close to the dew point, leaves get wet and disease risk rises.',
  },
  rssi: {
    key: 'rssi', label: 'Signal strength', short: 'RSSI', unit: 'dBm', color: '#64748b', decimals: 0, icon: 'Signal',
    help: 'Received LoRa signal strength. Closer to 0 is stronger; below -110 dBm the link becomes unreliable.',
  },
  snr: {
    key: 'snr', label: 'Signal-to-noise', short: 'SNR', unit: 'dB', color: '#94a3b8', decimals: 1, icon: 'Radio',
    help: 'How far the radio signal stands above the background noise. LoRa can decode down to about -20 dB, but above 0 dB the link is very comfortable.',
  },
};
export const CHART_METRICS = ['temperature', 'humidity', 'pressure', 'co2', 'battery', 'o2'];

export const STATUS = {
  online: { label: 'Online', tone: 'ok', help: 'Sending data, no open alert.' },
  warning: { label: 'Warning', tone: 'warn', help: 'Sending data, at least one measurement is outside its limits.' },
  critical: { label: 'Critical', tone: 'crit', help: 'A critical alert is open on this station.' },
  offline: { label: 'Offline', tone: 'off', help: 'No data received recently.' },
};

export const ALERT_TYPES = {
  temp_high: { label: 'High temperature', icon: 'ThermometerSun', rule: 'Temperature above the station maximum. Critical when 5 °C above.' },
  temp_low: { label: 'Low temperature', icon: 'Snowflake', rule: 'Temperature below the station minimum. Critical when 3 °C below (frost).' },
  hum_high: { label: 'High humidity', icon: 'CloudRain', rule: 'Humidity above the station maximum: fungal disease risk.' },
  hum_low: { label: 'Low humidity', icon: 'Sun', rule: 'Humidity below the station minimum: plants dry out quickly.' },
  battery_low: { label: 'Low battery', icon: 'BatteryWarning', rule: 'Battery under the safe voltage. Critical 0.4 V lower.' },
  co2_high: { label: 'High CO₂', icon: 'Cloud', rule: 'CO₂ above 1 000 ppm. Critical above 1 800 ppm.' },
  offline: { label: 'Station offline', icon: 'WifiOff', rule: 'No reading received for several minutes.' },
};

export const SCENARIO_HELP = {
  heatwave: 'Heat wave', frost: 'Frost night', storm: 'Storm front', humidity_spike: 'Humidity spike',
  low_battery: 'Low battery', co2_leak: 'CO₂ anomaly', offline: 'Station offline',
};
